import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, delay, finalize, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoginCredentials, RegisterCredentials, User, TokenResponse, RawUserResponse } from '../models/auth.model';
import { TokenService } from './token.service';
import { SKIP_AUTH } from '../tokens';
import { HttpContext } from '@angular/common/http';

export interface AuthResponse {
  user: RawUserResponse;
  accessToken: string;
  refreshToken: string;
  socialRegistered: boolean;
  socialMerged: boolean;
  socialLogged: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;
  private refreshInFlight$: Observable<TokenResponse> | null = null;

  constructor(
    private http: HttpClient,
    private tokenService: TokenService
  ) { }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, credentials, { context: new HttpContext().set(SKIP_AUTH, true) })
      .pipe(
        tap(response => {
          this.tokenService.setTokens(response.accessToken, response.refreshToken);
        })
      );
  }

  register(credentials: RegisterCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, credentials, { context: new HttpContext().set(SKIP_AUTH, true) })
      .pipe(
        tap(response => {
          this.tokenService.setTokens(response.accessToken, response.refreshToken);
        })
      );
  }

  refreshToken(): Observable<TokenResponse> {
    // If a refresh is already running, return the SAME observable.
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      console.error('No refresh token available');
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInFlight$ = this.http.post<TokenResponse>(
      `${this.apiUrl}/refresh`,
      { refreshToken },
      { context: new HttpContext().set(SKIP_AUTH, true) }
    ).pipe(
      tap(tokens => {
        // Update access token and refresh token if provided
        this.tokenService.setAccessToken(tokens.accessToken);
        // this.tokenService.setRefreshToken(refreshToken);
      }),
      catchError(error => {
        // If refresh fails, clear tokens and force re-login
        this.tokenService.removeTokens();
        if (error?.error?.message === 'Invalid refresh token') {
          return throwError(() => new Error('Invalid refresh token'));
        }
        return throwError(() => error);
      }),
      // Share one network call among all waiters.
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.refreshInFlight$ = null;
      })
    );

    return this.refreshInFlight$;
  }

  revokeToken(): Observable<void> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<void>(`${this.apiUrl}/revoke`, { refreshToken })
      .pipe(
        tap(() => {
          this.tokenService.removeTokens();
        })
      );
  }

  getCurrentUser(): Observable<RawUserResponse> {
    return this.http.get<RawUserResponse>(`${this.apiUrl}/me`);
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/forgot-password`, { email }, { context: new HttpContext().set(SKIP_AUTH, true) });
  }

  verifyResetToken(token: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/verify-reset-token`, { params: { token }, context: new HttpContext().set(SKIP_AUTH, true) });
  }

  resetPassword(token: string, password: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, { token, password }, { context: new HttpContext().set(SKIP_AUTH, true) });
  }

  verifyEmail(email: string, token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/verify-email`, { email, token }, { context: new HttpContext().set(SKIP_AUTH, true) });
  }
}
